import { UserIntentValue } from '@/models/common/user-intent/internal-models';
import { ChartMetric } from '@/types/userIntell/chartView';
import { humanizeString } from './string.utils';

const aggregatesTranslations: Record<'groupByInteraction' | 'groupByConversation', string> = {
  groupByInteraction: 'Interaction', //'Group by interaction'
  groupByConversation: 'Conversation',
} as const;


const userIntellMetricTranslations: Record<ChartMetric | 'dissatisfaction_rate', string> = {
  users: 'Users',
  interactions: 'Interactions',
  'avg-conversation-time': 'Average conversation time',
  'positive-feedback': 'Positive signals',
  retention: 'Retention',
  conversations: 'Conversations',
  'avg-interactions': 'Average interactions',
  error_rate: 'Error rate',
  dissatisfaction_rate: 'Dissatisfaction rate',
  sentiment_score: 'User sentiment',
  error_rate_explicit: 'Explicit error rate',
  bounce_rate: 'Bounce rate',
  cost: 'AI cost',
  latency_seconds: 'Latency',
  user_feedback_v2: 'User feedback',
  'negative-feedback': 'Error rate',
} as const;

const customAggregateCategoriesTranslations: Partial<Record<string, string>> = {
  'topic': 'Topic',
  'model_suggestion': 'LLM Problem',
  'action': 'Action',
  'category': 'Category',
} as const


const userIntentsTranslations: Record<UserIntentValue, string> = {
  other: 'Other minor tasks',
  informational: 'Informational',
  navigational: 'Navigational',
  'transactional/buying': 'Purchase intent',
  'customer support': 'Customer support',
  'exploratory/research': 'Exploratory / Research',
  'engagement/feedback': 'Engagement / Feedback',
  'complaint/dissatisfaction': 'Complaint / Dissatisfaction',
  'assistance': 'Assistance',
  'feedback and concerns': 'Feedback and Concerns',
  'transactional': 'Transactional',
} as const;

export const translations = {
  'userIntellMetric': userIntellMetricTranslations,
  'aggregates': aggregatesTranslations,
  'userIntents': userIntentsTranslations,
  'search': {
    'topic': 'Topic',
    'subtopic': 'Sub-category',
    'user_action': 'User action',
    'interaction': 'Raw interaction',
    'tag': 'Tag',
    'emotion': 'User emotion',
    'sentiment': 'User sentiment',
    'failure': 'LLM issue',
    'user_intent': 'User intent',
    'emotions': 'User emotions',
  },
  'create-new': {
    'topic': 'Topic',
    'llm_issue': 'Type of failure',
    'business_risk': 'Business risk',
    'intent': 'Intent',
  },
  'platform': {
    'homepage': 'Homepage',
    'reports': 'Reports',
    'all_reports': 'Reports',
    'live_activity': 'Live Data',
    'taxonomy': 'Taxonomy',
    'userIntelligence': 'User Intelligence',
    'userIntelligenceV2': 'USER INTELLIGENCE',
    'topics': 'Topics',
    'manuals_topics': 'Manuals',
    'manuals_category': 'Components',
    'manuals_user_action': 'User actions',
    'topic': 'Topic',
    'customTopics': 'Custom topics',
    'userIntents': 'User intents',
    'userIntent': 'User intent',
    'subintent': 'Taxonomy - Tier 2',
    'intentActions': 'Taxonomy - Tier 3',
    'categories': 'Sub-category',
    'userActions': 'User actions',
    'userAction': 'User action',
    'userRetention': 'User retention',
    'riskyBehaviors': 'Business risks',
    'sentimentAnalysis': 'User sentiment',
    'userEmotion': 'User emotion',
    'dropOffAnalysis': 'User flow & Drop-off',
    'retentionTrends': 'Retention trends',
    'userProblemsWithLLMs': 'User problems with LLMs',
    'areasOfImprovement': 'Areas of improvement',
    'rootCauseExplorer': 'FAILURES INTELLIGENCE',
    'outOfScope': 'Out of scope questions',
    'poorPromptQuality': 'Poor prompt quality',
    'listOfProblems': 'List of problems',
    'typeOfProblem': 'Problem',
    'tags': 'Tags',
    'userABTest': 'User A/B Test',
    'audiences': 'Audiences',
    'cohorts': 'User groups',
    'yourAudiences': 'Your audiences',
    'users': 'Users',
    'interactions': 'Interactions',
    'rawInteractions': 'Interactions',
    'usersInteractions': 'Users & Conversations',
    'whatsNew': 'What\'s New',
    'settings': 'Settings',
    'sentiment': 'User sentiment',
    'emotion': 'User emotion',
    'emotions': 'User emotions',
    'dataSource': 'RAG source',
    'favorites': 'Favorites',
    'keywords': 'Keywords',
    'keyword': 'Keywords',
    'search': 'Search',
    'create-new': 'Create new',
    'general': 'GENERAL',
    'favoriteReports': 'MY FAVORITES',
    'defaultReports': 'ANALYTICS',
    'workspace': 'WORKSPACE',
    'privateReports': 'PRIVATE',
    'reportsLibrary': 'Reports Library',
  },
  'common': {
    'all': 'All',
    'user_query': 'User query',
    'intent': 'User intent',
    'user_intent': 'User intent',
    'topic': 'Topic',
    'conversation': 'Conversation',
    'user_feedback': 'User feedback',
    'date': 'Date',
    'user': 'User',
    'retention': 'Retention',
    'min': 'min',
    'groupBy': 'Group by',
    'noGroupsAvailable': 'No groups available',
    'searchEllipsis': 'Search...',
    'noDataAvailable': 'No data available',
    'selectAll': 'Select all',
    'sortBy': 'Sort by',
    'saveAsNewProject': 'Save as a new project',
    'createNewProject': 'Create new project',
    'projectName': 'Project name',
    'cancel': 'Cancel',
    'confirm': 'Confirm',
    'timerange': 'Timerange',
    'globalFilters': 'Global filters',
    'organization': 'Organization',
  },
  'userIntelligence': {
    'table': 'Table',
    'chart': 'Trend',
    'map': 'Map',
    'primaryMetric': 'Primary metric',
    'secondaryMetric': 'Secondary metric',
    'user-query': 'User query',
    'interactions': 'Interactions',
    'hide': 'Hide',
    'show': 'Show',
  },
  'time': {
    'hour': 'Hour',
    'day': 'Day',
    'week': 'Week',
    'month': 'Month',
    'year': 'Year',
    'period': 'Period',
    'startDate': 'Start date',
    'endDate': 'End date',
  },
  'problemsIdentificationSources': {
    'llm_self_aknowledge': 'Agents/LLMs answers and steps',
    'implicit_frustration': 'Implicit user feedback',
    'thumbs_down': 'Explicit user feedback',
  },
  'variable': {
    'subtopic': 'Sub-category',
    'error_rate': 'Error rate',
    'dissatisfaction_rate': 'Dissatisfaction rate',
    'rag_details': 'RAG details',
    'error_rate_explicit': 'Explicit error rate',
    'conversation_error_rate_explicit': 'Explicit error rate',
    'bounce_rate': 'Bounce rate',
    'users': 'Users',
    'alphabetical': 'Name',
    'interactions': 'Interactions',
    'retention': 'Retention',
    'cost': 'AI cost',
    'latency': 'Latency',
    'satisfaction': 'Satisfaction',
    'warnings': 'User feedback',
    'frustration': 'Frustration',
    'text_language': 'Language',
    'source': 'RAG source',
    'sentiment': 'User sentiment',
    'conversations': 'Conversations',
    'conversation_error_rate': 'Error rate',
    'conversation_dissatisfaction_rate': 'Dissatisfaction rate',
    'n_sessions': 'Conversations',
    'conversation_length': 'Conversation length',
    'roi_hours_saved': 'Total hours saved',
    'roi_hours_saved_per_user': 'Hours saved per user',
    'roi_hours_saved_per_interaction': 'Hours saved per task',
    'roi_successful_interactions': 'Tasks completed',
    'roi_success_rate': 'Task completion rate',
    'roi_value_saved': 'Total value saved ($)',
    'roi_value_saved_per_interaction': 'Value saved ($) per task',
    'prompts_to_value': 'Prompts to value',
    'conversation_time': 'Duration',
    'session_time': 'Avg. Conversation time',
    'conversation_session_length': 'Avg. Interactions',
    'session_length': 'Avg. Interactions',
    'session_length_conversations': 'Avg. Interactions',
    'start_session': 'Start time',
    'end_session': 'End time',
    'intents': 'User intents',
    'intent': 'User intent',
    'user_intent': 'User intent',
    'user_actions': 'User actions',
    'user_action': 'User action',
    'emotions': 'User emotions',
    'sentiment_score': 'Avg. User sentiment',
    'negative_feedback': 'LLM issues',
    'positive_feedback': 'Positive signals',
    'positive_messages': 'Positive user sentiment',
    'negative_messages': 'Negative user sentiment',
    'negative_imp_feedback': 'Negative implicit feedback',
    'negative_exp_feedback': 'Thumbs down',
    'positive_imp_feedback': 'Positive implicit feedback',
    'positive_exp_feedback': 'Thumbs up',
    'explicit_feedback_ratio': 'Thumbs up / down',
    'conversation_sentiment': 'Conversation User sentiment',
    'conversation_sentiment_score': 'Avg. User sentiment',
    'topics_conversations': 'Topic',
    'timestamp': 'Timestamp',
    'input_text': 'Input',
    'output_text': 'Output',
    'end_user': 'User',
    'type_of_problem_mid': 'Type of problem (Mid-level)',
    'type_of_problem_v2': 'Type of failures',
    'type_of_risk': 'Type of risk',
    'feedback_type_v2': 'User feedback',
    'paste_output': 'Copy / paste',
    'return_rate': 'Return rate',
    'negative_intent': 'Business risks',
    'negative_user_intent_v2': 'Business risks',
    'conversation_id': 'Conversation ID',
    'error_status': 'Conversation Health',
    'topic': 'Topic',
    'dialogue_turns': 'Dialogue turns',
    'duration': 'Duration',
    'dropoff_sentiment': 'Drop-off Sentiment',
    'penetration': 'Activation rate',
    'feedback_action_rating': 'User rating',
    'occurrences': 'Occurrences',
    'problems': 'Problems',
    'input_length': 'Input length',
    'output_length': 'Output length',
    'input_tokens': 'Input tokens',
    'output_tokens': 'Output tokens',
    'total_users': 'Total users',
    'aifi_score': 'AI Proficiency',
  },
  'variable_conversations': {
    'interactions': 'Dialogue turns',
    'input_length': 'Input length (avg)',
    'output_length': 'Output length (avg)',
  },
  'id': {
    'conversationId': 'Conversation ID',
    'userId': 'User ID',
  },
  'retentionPage': {
    'addUserGroup': 'Add user group',
  },
  'variable_queries': {
    'session_length': 'Interactions',
    'sentiment': 'Drop-off Sentiment',
  },
  'sentiment': {
    'neutral': 'Neutral',
    'mixed': 'Mixed',
    'positive': 'Positive',
    'negative': 'Negative',
    'very_positive': 'Very positive',
    'very_negative': 'Very negative',
  },
  'conversation_sentiment': {
    'neutral': 'Neutral',
    'positive': 'Positive',
    'negative': 'Negative',
    'very_positive': 'Very positive',
    'very_negative': 'Very negative',
  },
  'emotions': {
    'admiration': 'Admiration',
    'relief': 'Relief',
    'amusement': 'Amusement',
    'gratitude': 'Gratitude',
    'satisfaction': 'Satisfaction',
    'interest': 'Interest',
    'thankfulness': 'Thankfulness',
    'trust': 'Trust',
    'surprise': 'Surprise',
    'appreciation': 'Appreciation',
    'joy': 'Joy',
    'none': 'None',
    'criticism': 'Criticism',
    'disapproval': 'Disapproval',
    'frustration': 'Frustration',
    'fear': 'Fear',
    'anger': 'Anger',
    'hate': 'Hate',
    'violence': 'Violence',
    'sexual_desire': 'Sexual Desire',
    'love': 'Love',
    'confusion': 'Confusion',
    'annoyance': 'Annoyance',
    'disappointment': 'Disappointment',
    'disgust': 'Disgust',
    'tension': 'Tension',
    'regret': 'Regret',
    'concern': 'Concern',
    'sadness': 'Sadness',
    'uncertainty': 'Uncertainty',
    'disinterest': 'Disinterest',
    'anxiety': 'Anxiety',
    'apprehension': 'Apprehension',
    'skepticism': 'Skepticism',
  },
  'report_type': {
    'analytics': 'Analytics',
    'conversion': 'Conversion',
    'engagement': 'Engagement',
    'frustration': 'Frustration',
    'user_flow': 'User Flow',
    'retention': 'Retention',
  },
  'operation': {
    'eq': 'Equals',
    'neq': 'Not equals',
    'gt': 'Greater than',
    'gte': 'Greater than or equal to',
    'lt': 'Less than',
    'lte': 'Less than or equal to',
    'is': 'Is',
    'isNot': 'Is not',
    'startsWith': 'Starts tith',
    'contains': 'Contains',
  },
  'breakdown': {
    'topic': 'Topic',
    'data_source': 'RAG source',
    'action': 'Action',
    'user': 'User',
    'frustration_reason': 'Frustration reason',
    'user_satisfaction': 'User satisfaction',
    'pii': 'PII',
    'cohort': 'User group',
    'user_interactions_per_day': 'User interactions per day',
    'warning_category': 'Warning category',
    'warning_subcategory': 'Warning subcategory',
    'category': 'Category',
    'keywords': 'Keywords',
    'favorites': 'Favorites',
  },
  'provider': {
    'openai': 'OpenAI',
    'cohere': 'Cohere',
    'hugging_face': 'Hugging Face',
    'anthropic': 'Anthropic',
    'google_palm': 'Google PaLM 2',
    'langchain': 'LangChain',
  },
  'type_of_problem': {
    'poor prompt quality': 'Poor prompt quality',
    'out of scope': 'Out of scope / instruction',
    'mcp/connection errors': 'MCP/connection errors',
  },
  roles: {
    admin: 'Admin',
    member: 'Member',
    'viewer': 'Viewer',
    'nebulyadmin': 'Nebuly Admin',
    'projectadmin': 'Project Admin',
  },
  'visualization_type': {
    'linear': 'Linear',
    'rolling': 'Rolling',
    'cumulative': 'Cumulative',
    'time_series': 'Time series',
    'frequency_curve': 'Frequency curve',
    'average': 'Average',
    'median': 'Median',
    'min': 'Min',
    'max': 'Max',
    'formula': 'Formula',
  },
  'action': {
    'add': 'Add',
    'remove': 'Remove',
    'click': 'Click',
  },
  'granularity': {
    'hour': 'Hour',
    'day': 'Day',
    'week': 'Week',
    'month': 'Month',
    'quarter': 'Quarter',
    'year': 'Year',
    'full': 'Full',
  },
  'rolling_window': {
    'label': 'Rolling window',
    'size': 'Window size',
    'unit': 'Unit',
    'off': 'Off',
    'day': 'Days',
    'week': 'Weeks',
    'month': 'Months',
    'description': 'Each point uses data from the last N days, weeks, or months.',
    'disabled_hint': 'Turn on to replace fixed calendar buckets with a sliding window.',
    'size_required': 'Enter a window size.',
    'size_invalid': 'Enter a whole number.',
    'size_positive': 'Window size must be at least 1.',
    'size_too_large': 'Window size is too large.',
    'size_too_large_day': 'Window size is too large. Maximum is 365 days.',
    'size_too_large_week': 'Window size is too large. Maximum is 52 weeks.',
    'size_too_large_month': 'Window size is too large. Maximum is 12 months.',
  },
  'distribution_range': {
    'day': 'Day',
    'week': 'Week',
    'month': 'Month',
    'year': 'Year',
  },
  'total_percentage': {
    'total': 'Count',
    'percentage': 'Percentage',
  },
  'chart_kind': {
    'line_chart': 'Time series',
    'horizontal_bar': 'Horizontal bar chart',
    'vertical_bar': 'Stacked time series',
    'clustered_bar': 'Clustered bar chart',
    'bar_chart': 'Bar chart',
    'area_chart': 'Area chart',
    'metric': 'Metric',
    'gauge': 'Gauge',
    'distributed': 'Time series distribution',
    'scatter': 'Scatter plot',
  },
  'action_slug': {
    'search_new_information': 'Search new information',
    'generate_new_content': 'Generate new content',
    'ask_to_improve_previous_answer': 'Ask to improve previous answer',
    'positive_user_comment': 'Positive user comment',
    'negative_user_comment': 'Negative user comment',
    'begin_conversation': 'Begin conversation',
    'follow_up': 'Follow up',
    'other': 'Other',
    'drop_off': 'Drop off',
    'no_action': 'No action',
    'thumbs_up': 'Thumbs up',
    'thumbs_down': 'Thumbs down',
    'copy_input': 'Copy input',
    'copy_output': 'Copy output',
    'paste': 'Paste to input',
    'comment': 'Comment',
    'regenerate': 'Regenerate',
    'edit': 'Edit',
    'search_specific_information': 'Search specific information',
    'generate_content': 'Generate content',
    'rating': 'User rating'
  },
  'frustration_cause': {
    'verbosity': 'Verbosity',
    'limited_knowledge': 'Limited knowledge',
    'lack_of_understanding': 'Lack of understanding',
    'lack_of_clarity': 'Lack of clarity',
    'lack_of_depth': 'Lack of depth',
    'not_found': 'Not found',
    'no_frustration': 'No frustration',
  },
  'filter': {
    'filterBy': 'Filter by',
    'groupBy': 'Group by',
    'applied': ' applied',
    'topic': 'Topic',
    'manuals_topic': 'Manuals topic',
    'manuals_category': 'Manuals category',
    'manuals_user_action': 'Manuals user action',
    'data_source': 'RAG source',
    'language': 'Language',
    'user': 'User',
    'action': 'Action',
    'frustration_reason': 'Frustration reason',
    'user_satisfaction': 'User satisfaction',
    'pii': 'PII',
    'tag': 'Tag',
    'is_segment_start': 'Is segment start',
    'is_negative_intent': 'Is behavioral alert',
    'negative_intent': 'Business risks',
    'negative_user_intent_v2': 'Business risks',
    'cohort': 'User group',
    'feedback_action': 'User feedback',
    'is_frustrated': 'Frustration',
    'is_frustrated_v2': 'Frustration',
    'first_interaction': 'First interaction',
    'last_interaction': 'Last interaction',
    'last_interaction_tag': 'Interaction tag',
    'users_interactions': 'Number of interactions',
    'user_conversations_count': 'Number of conversations',
    'user_conversation_length': 'Conversation turns',
    'active_periods': 'Active periods',
    'avg_session_length': 'Avg session length',
    'intent': 'User intent',
    'user_action': 'User action',
    'user_actions': 'User Actions',
    'warning_category': 'Warning category',
    'warning_category_v2': 'LLM Issue',
    'warning_subcategory': 'Warning subcategory',
    'warning_type': 'Warning type',
    'subject': 'Subject',
    'feedback_type': 'User feedback', // @deprecated
    'feedback_type_v2': 'User feedback',
    'topic_keywords': 'Topic keywords',
    'sentiment': 'User sentiment',
    'conversation_sentiment': 'Avg. user sentiment',
    'emotions': 'User emotions',
    'category': 'Category',
    'user_interactions_per_day': 'User interactions per day',
    'favorite': 'Favorite',
    'favorites': 'Favorites',
    'type_of_problem_mid': 'Type of problem (Mid-level)',
    'type_of_problem_v2': 'Type of failures',
    'has_user_comment': 'User comment',
    'user_rating': 'User rating',
    'type_of_risk': 'Type of risk',
    'negative_intent_v2': 'Risky behavior',
    'conversation': 'Conversation',
    'conversation_category': 'Conversation category',
    'conversation_custom_aggregate': 'Conversation custom aggregate',
    'conversation_id': 'Conversation ID',
    'conversation_manuals_category': 'Conversation manuals category',
    'conversation_manuals_topic': 'Conversation anuals topic',
    'conversation_manuals_user_action': 'Conversation anuals user action',
    'conversation_topic': 'Conversation topic',
    'conversation_user_action': 'Conversation user action',
    'conversation_user_query': 'Conversation user query',
    'conversation_error_status': 'Conversation Health',
    'action_replacement': 'Action replacement',
    'custom_aggregate': 'Custom aggregate',
    'has_data_source': 'Has RAG source',
    'has_emotion': 'Has emotion',
    'has_llm_issue': 'Has LLM issue',
    'intent_action': 'Intent action',
    'is_negative_intent_v2': 'Has risky behavior',
    'keyword': 'Keyword',
    'positive_comment': 'Positive comment',
    'conversation_session_length': 'Dialogue turns',
    'subintent': 'Subintent',
    'user_feedback_v2': 'User feedback',
    'user_intent': 'User intent',
    'user_query': 'User query',
    'penetration': 'Activation rate',
    'out_of_scope_suggestion': 'Out of scope questions',
    'pii_processed': 'PII processed',
  },
  'filter_tabs': {
    'all': 'All',
    'interactions': 'Interactions',
    'conversations': 'Conversations',
    'users': 'Users',
    'user_groups': 'User groups',
    'tags': 'Tags',
    'custom_columns': 'Custom columns',
    'conversation_custom_columns': 'Conversation labels',
    'custom_aggregates': 'Custom aggregates',
    'conversation_custom_aggregates': 'Conversation custom aggregates',
    'saved_filters': 'Saved filters',
    'favorites': 'Favorites',
    'recently_used': 'Recently used',
    'time': 'Time',
  },
  "interaction_properties": {
    "cost": "AI cost",
    "latency": "Latency",
    "text_language": "Language",
    "verbosity": "Character amount",
    "input_length": "Input length",
    "feedback_action_text": "User comment",
    "feedback_action_rating": "User rating",
    "feedback_actions": "User feedback",
    "pii_removed": "PII",
    "sentiment": "User sentiment",
    "emotions": "User emotions",
  },
  'user_satisfaction': {
    'positive': 'Satisfied',
    'neutral': 'Neutral',
    'negative': 'Dissatisfied',
  },
  'usage_kind': {
    'on_demand': 'On-Demand',
    'spot': 'Spot',
    'committed_1yr': 'Committed 1 Year',
    'committed_3yr': 'Committed 3 Year',
  },
  'pricing_plan': {
    'trial': 'Free Trial',
    'starter_monthly': 'Starter monthly',
    'starter_annual': 'Starter annual',
    'growth_monthly': 'Growth monthly',
    'growth_annual': 'Growth annual',
    'enterprise': 'Enterprise',
    'personal': 'Personal',
    'action': 'Action',
  },
  'pii': {
    'yes': 'Yes',
    'no': 'No',
  },
  'interaction_filter': {
    'topic': 'Topic',
    'data_source': 'RAG source',
    'language': 'Language',
    'user': 'User',
    'frustration_reason': 'Frustration reason',
    'action': 'Action',
  },
  'date_config_kind': {
    'in_the_last': 'In the last',
    'between': 'Between',
    'after': 'After',
    'before': 'Before',
  },
  'value_config_kind': {
    'below': 'Fewer than',
    'above': 'More than',
    'between': 'Between',
    'exactly': 'Exactly',
  },
  'login': {
    'login_user_disabled': 'Your user is disabled. Please contact support@nebuly.ai for further information.',
    'login_bad_credentials': 'Invalid credentials',
    'login_invalid_auth_provider': 'You are trying to login with an invalid auth provider',
  },
  'warnings': {
    'unassigned': 'Untagged',
  },
  'metric': {
    'num_warnings': 'User problems w/ LLMs',
    'segment_length': 'Segment length',
    'positive_user_actions': 'Thumbs up',
    'negative_user_actions': 'Thumbs down',
    'positive_user_comments': 'Positive user comments',
    'negative_user_comments': 'Negative user comments',
    'daily_interactions': 'Daily interactions',
    'cost': 'AI cost',
    'latency': 'Latency',
    'negative_messages': 'Negative / Very Negative sentiment',
    'positive_messages': 'Positive / Very Positive sentiment',
    'user_rating': 'User rating',
  },
  'health_status': {
    'first_event_received': 'First event received',
    'metrics_available': 'Metrics available',
    'exposures_balanced': 'Exposures balanced',
  },
  'abtesting_error_codes': {
    'project_not_found': 'Project not found',
    'experiment_not_found': 'Experiment not found',
    'experiment_invalid_status': 'Experiment invalid status',
    'feature_flag_not_found': 'Feature flag not found',
    'duplicate_feature_flag_name': 'Duplicate feature flag name',
  },
  'unhealthy_experiment_reason': {
    'no_events': 'No events',
    'metrics_not_available': 'Metrics not available',
    'exposure_issues': 'Exposure issues',
    'imbalanced_groups': 'Imbalanced groups',
    'low_p_value': 'Low p value',
  },
  'metric_name': {
    'num_warnings': 'User feedback',
    'segment_length': 'Segment length',
    'positive_user_actions': 'Thumbs up',
    'negative_user_actions': 'Thumbs down',
    'positive_user_comments': 'Positive user comments',
    'negative_user_comments': 'Negative user comments',
    'daily_interactions': 'Daily interactions',
    'cost': 'AI cost',
    'latency': 'Latency',
  },
  'notification_kind': {
    'new_warning': 'New warning',
    'new_warning_for_rule': 'New warning for rule',
    'new_intent': 'New user intent',
    'new_user_action': 'New user action',
    'new_interaction_intent': 'New interaction user intent',
  },
  'feedback_type': {
    'negative_implicit': 'Negative implicit feedback',
    'negative_explicit': 'Thumbs down',
    'positive_implicit': 'Positive implicit feedback',
    'positive_explicit': 'Thumbs up',
    "rating": 'Rating',
  },
  'feedback_type_v2': {
    'negative_implicit': 'Negative implicit feedback',
    'negative_explicit': 'Thumbs down',
    'positive_implicit': 'Positive implicit feedback',
    'positive_explicit': 'Thumbs up',
    "rating": 'Rating',
  },
  'feedback_type_chip': {
    'negative_implicit': 'Negative implicit feedback',
    'negative_explicit': 'Thumbs down',
    'positive_implicit': 'Positive implicit feedback',
    'positive_explicit': 'Thumbs up',
    "rating": 'Rating',
  },
  'error_messages': {
    'reset_password_bad_token': 'Invalid token',
    'weak_password': 'Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, and one number or special character.',
  },
  'rag': {
    ragSources: 'Rag Sources',
    'noRagSources': 'No rag sources for this interaction',
  },
  'settingsPageTabs': {
    members: 'Members',
    organizations: 'Organizations',
    projects: 'Projects',
    reports: 'Reports',
    alert_settings: 'Alerts',
    account: 'Account',
    shared_pages: 'Shared pages',
    interactions_access: 'Interactions access',
    filters: 'Allowed filters',
    integration: 'Integrations',
    remote_access: 'Remote Access',
    roi: 'ROI',
    feature_flags: 'Feature Flags',
    taxonomy: 'Taxonomy',
  },
  'liveActivityPageTabs': {
    interactions: 'Interactions',
    conversations: 'Conversations',
    users: 'Users',
    cohorts: 'User groups',
  },
  'customAggregateCategories': customAggregateCategoriesTranslations,
  'cohortSystemKind': {
    'aifi_proficiency': 'AI Proficiency',
  },
  'tenure': {
    under_1_year: 'Under 1 year',
    '1_to_3_years': '1 to 3 years',
    '3_to_5_years': '3 to 5 years',
    '5_to_10_years': '5 to 10 years',
    '10_plus_years': '10+ years',
  },
} as const;

/** Maps API shorthand like `1_3_years` to canonical `1_to_3_years`. */
export function normalizeTenureKey(key: string): string {
  const rangeMatch = /^(\d+)_(\d+)_years$/.exec(key);
  if (rangeMatch) return `${rangeMatch[1]}_to_${rangeMatch[2]}_years`;
  return key;
}

export const translate = (group: TranslationGroup | null | undefined, key: string) => {
  if (!group) return humanizeString(key);
  const lookupKey = group === 'tenure' ? normalizeTenureKey(key) : key;
  return (translations[group] as { [key: string]: string })?.[lookupKey] || humanizeString(key);
};

type Translation = typeof translations
export type TranslationGroup = keyof typeof translations

/**
 * typesafe version of translate()
 *
 * @param group
 * @param key
 */
export const translateSafe = <TGroup extends TranslationGroup>(group: TGroup, key: keyof Translation[TGroup]): string => {
  return translations[group][key] as string;
};

export function sortByTranslatedVersion(translationGroup: TranslationGroup, direction: 'asc' | 'desc' = 'asc') {
  return (a: string, b: string) => {
    const translatedA = translate(translationGroup, a);
    const translatedB = translate(translationGroup, b);

    return direction === 'asc' ? translatedA.localeCompare(translatedB) : translatedB.localeCompare(translatedA);
  };
}
